import stringSimilarity from 'string-similarity';
import DeviceDetector from 'device-detector-js';

/**
 * خدمة مقارنة الأجهزة وتحديد ما إذا كانت متطابقة
 */
export const deviceMatchingService = {
  /**
   * معامل التشابه المطلوب لاعتبار الأجهزة متطابقة
   */
  SIMILARITY_THRESHOLD: 0.8,

  /**
   * كائن DeviceDetector لتحليل معلومات الجهاز
   */
  deviceDetector: new DeviceDetector(),

  /**
   * الحصول على معلومات الجهاز الأساسية للواجهة API
   * @returns {Object} معلومات الجهاز الأساسية
   */
  getDeviceInfo() {
    try {
      const userAgent = navigator.userAgent;
      const deviceInfo = this.deviceDetector.parse(userAgent);
      
      // التأكد من أن جميع الحقول هي نصوص وليست كائنات
      return {
        type: deviceInfo.device?.type || 'unknown',
        os: typeof deviceInfo.os === 'string' ? deviceInfo.os : (deviceInfo.os?.name || 'unknown'),
        browser: typeof deviceInfo.client === 'string' ? deviceInfo.client : (deviceInfo.client?.name || 'unknown'),
        region: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      };
    } catch (error) {
      console.error('Error getting basic device info:', error);
      return {
        type: 'unknown',
        os: 'unknown',
        browser: 'unknown',
        region: ''
      };
    }
  },

  /**
   * الحصول على معلومات الجهاز التفصيلية
   * @returns {Object} معلومات الجهاز التفصيلية
   */
  getDetailedDeviceInfo() {
    try {
      const userAgent = navigator.userAgent;
      const deviceInfo = this.deviceDetector.parse(userAgent);
      
      return {
        client: deviceInfo.client,
        os: deviceInfo.os,
        device: deviceInfo.device,
        bot: deviceInfo.bot,
        brand: deviceInfo.device?.brand,
        model: deviceInfo.device?.model,
        type: deviceInfo.device?.type,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        colorDepth: window.screen.colorDepth,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        cores: navigator.hardwareConcurrency,
        memory: navigator.deviceMemory,
        connection: navigator.connection?.effectiveType,
      };
    } catch (error) {
      console.error('Error getting detailed device info:', error);
      return {};
    }
  },

  /**
   * إنشاء بصمة موسعة للجهاز تجمع بين FingerprintJS ومعلومات الجهاز التفصيلية
   * @param {string} fingerprint بصمة FingerprintJS
   * @returns {Object} البصمة الموسعة
   */
  createEnhancedFingerprint(fingerprint) {
    const deviceInfo = this.getDetailedDeviceInfo();
    
    return {
      fingerprint,
      deviceInfo,
      timestamp: new Date().toISOString(),
    };
  },

  /**
   * تحليل معلومات الجهاز بشكل أكثر تفصيلاً لتحسين دقة المطابقة
   * @returns {Object} معلومات الجهاز المحللة
   */
  async analyzeDevice() {
    try {
      const deviceInfo = this.getDetailedDeviceInfo();
      
      // محاولة الحصول على معلومات إضافية عن الجهاز
      const additionalInfo = {
        // معلومات المتصفح المفصلة
        browser: {
          cookiesEnabled: navigator.cookieEnabled,
          doNotTrack: navigator.doNotTrack,
          plugins: Array.from(navigator.plugins || []).map(p => p.name).slice(0, 5),
          userAgent: navigator.userAgent,
        },
        
        // معلومات الشاشة
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          colorDepth: window.screen.colorDepth,
          pixelRatio: window.devicePixelRatio,
          orientation: window.screen.orientation?.type || 'unknown',
        },
        
        // معلومات المنطقة الزمنية واللغة
        locale: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          languages: Array.from(navigator.languages || []),
          timezoneOffset: new Date().getTimezoneOffset(),
        },
        
        // معلومات الأجهزة
        hardware: {
          platform: navigator.platform,
          cores: navigator.hardwareConcurrency,
          memory: navigator.deviceMemory,
          maxTouchPoints: navigator.maxTouchPoints,
        },
        
        // معلومات الشبكة
        network: {
          connectionType: navigator.connection?.type,
          effectiveType: navigator.connection?.effectiveType,
          downlink: navigator.connection?.downlink,
          rtt: navigator.connection?.rtt,
        }
      };
      
      return {
        ...deviceInfo,
        ...additionalInfo,
        analysisTimestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error analyzing device:', error);
      return this.getDetailedDeviceInfo();
    }
  },

  /**
   * حساب درجة التشابه بين بصمتين
   * @param {Object} fingerprint1 البصمة الأولى
   * @param {Object} fingerprint2 البصمة الثانية
   * @returns {number} درجة التشابه (0-1)
   */
  calculateSimilarity(fingerprint1, fingerprint2) {
    try {
      // مقارنة بصمة FingerprintJS
      const fpSimilarity = stringSimilarity.compareTwoStrings(
        fingerprint1.fingerprint || '',
        fingerprint2.fingerprint || ''
      );
      
      // مقارنة نوع الجهاز
      const typeSimilarity = fingerprint1.deviceInfo?.device?.type === fingerprint2.deviceInfo?.device?.type ? 1 : 0;
      
      // مقارنة نظام التشغيل
      const osSimilarity = fingerprint1.deviceInfo?.os?.name === fingerprint2.deviceInfo?.os?.name ? 1 : 0;
      
      // مقارنة دقة الشاشة
      const screenSimilarity = 
        fingerprint1.deviceInfo?.screenWidth === fingerprint2.deviceInfo?.screenWidth &&
        fingerprint1.deviceInfo?.screenHeight === fingerprint2.deviceInfo?.screenHeight ? 1 : 0;
      
      // مقارنة المنطقة الزمنية
      const timezoneSimilarity = fingerprint1.deviceInfo?.timezone === fingerprint2.deviceInfo?.timezone ? 1 : 0;
      
      // حساب المتوسط المرجح
      const weightedSimilarity = (
        fpSimilarity * 0.5 +
        typeSimilarity * 0.15 +
        osSimilarity * 0.15 +
        screenSimilarity * 0.1 +
        timezoneSimilarity * 0.1
      );
      
      return weightedSimilarity;
    } catch (error) {
      console.error('Error calculating similarity:', error);
      return 0;
    }
  },

  /**
   * التحقق مما إذا كانت البصمتان متطابقتان
   * @param {Object} fingerprint1 البصمة الأولى
   * @param {Object} fingerprint2 البصمة الثانية
   * @param {string} ip1 عنوان IP الأول
   * @param {string} ip2 عنوان IP الثاني
   * @returns {boolean} هل البصمتان متطابقتان
   */
  areDevicesMatching(fingerprint1, fingerprint2, ip1, ip2) {
    try {
      // حساب التشابه بين البصمتين
      const similarity = this.calculateSimilarity(fingerprint1, fingerprint2);
      
      // التحقق من تطابق عنوان IP
      const ipMatch = ip1 === ip2;
      
      // التحقق من تطابق المنطقة الزمنية
      const timezoneMatch = fingerprint1.deviceInfo?.timezone === fingerprint2.deviceInfo?.timezone;
      
      // قواعد المطابقة
      const matchByFingerprint = similarity >= this.SIMILARITY_THRESHOLD;
      const matchByIpAndTimezone = ipMatch && timezoneMatch;
      
      return matchByFingerprint || matchByIpAndTimezone;
    } catch (error) {
      console.error('Error checking device match:', error);
      return false;
    }
  }
};
